import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import { Skeleton } from '@material-ui/lab';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PaginationActionsTableOmni from './PaginationActionsTableOmni';
import { useCallback } from 'react';

const useStyles = makeStyles(theme => ({
	paper: {
		backgroundColor: `${theme.palette.background.paper} !important`,
		maxWidth: '100vw',
		borderRadius: '16px !important',
		border: `1px solid ${theme.palette.divider}`,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	header: {
		backgroundColor: '#102239 !important',
		position: 'sticky',
		top: 0,
		zIndex: 2
	},
	tableCell: {
		whiteSpace: 'nowrap'
	},
	headerCell: {
		color: `${theme.palette.common.white} !important`,
		fontSize: '1.1rem !important',
		backgroundColor: `${theme.palette.background.tableHeader} !important`
	},
	sortLabel: {
		color: `${theme.palette.common.white} !important`,
		'&:hover': {
			color: `${theme.palette.common.white} !important`
		},
		'&.Mui-active': {
			color: `${theme.palette.common.white} !important`
		}
	},
	tableContainer: {
		borderRadius: '16px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column'
	},
	tableWrapper: {
		overflow: 'auto'
	},
	table: {
		minWidth: '100%',
		fontSize: '18px !important'
	},
	stickyHeader: {
		position: 'sticky',
		top: 0,
		zIndex: 1
	},
	pagination: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		zIndex: 1
	},
	checkbox: {
		color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		'&.Mui-checked': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		},
		'&.MuiCheckbox-indeterminate': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		}
	}
}));

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
	const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, checkBox, expandable } = props;
	const classes = useStyles(props);
	const theme = useTheme(); // Add useTheme hook

	const createSortHandler = property => event => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow className={classes.header}>
				{expandable && <TableCell className={classes.headerCell} padding="checkbox" />}
				{checkBox && (
					<TableCell style={{ backgroundColor: theme.palette.background.tableHeader}} padding="checkbox">
						<Checkbox
							indeterminate={numSelected > 0 && numSelected < rowCount}
							checked={rowCount > 0 && numSelected === rowCount}
							onChange={onSelectAllClick}
							inputProps={{
								'aria-label': 'select all'
							}}
							className={classes.checkbox}
							style={{
								color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
							}}
						/>
					</TableCell>
				)}
				{headCells.map(headCell => (
					<TableCell
						classes={{ root: classes.tableCell }}
						key={headCell.id}
						align={headCell.numeric ? 'center' : 'left'}
						padding={headCell.disablePadding ? 'none' : 'normal'}
						sortDirection={orderBy === headCell.id ? order : false}
						className={classes.headerCell}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : 'asc'}
							onClick={createSortHandler(headCell.id)}
							className={classes.sortLabel}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	numSelected: PropTypes.number,
	onRequestSort: PropTypes.func.isRequired,
	onSelectAllClick: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
	headCells: PropTypes.array.isRequired
};

function EnhancedTableToolbar(props) {
	const { numSelected, title } = props;
	return (
		<Toolbar
			sx={[
				{
					pl: { sm: 2 },
					pr: { xs: 1, sm: 1 }
				},
				numSelected > 0 && {
					bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
				}
			]}
		>
			{numSelected > 0 ? (
				<Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
					{numSelected} selected
				</Typography>
			) : (
				<Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
					{title}
				</Typography>
			)}
		</Toolbar>
	);
}

EnhancedTableToolbar.propTypes = {
	numSelected: PropTypes.number.isRequired,
	title: PropTypes.string
};

function TableOmni(
	{
		rows,
		headCells,
		title,
		tableName,
		checkBox,
		selectedRows,
		setSelectedRows,
		setCurrentParams,
		perPageRows,
		curPage,
		totalItems,
		onEditClick,
		onDeleteClick,
		isLoadingPagination,
		rowKey,
		height = 'auto',
		rowsPerPageOptions=[5, 10, 25],
		expandable = false,
		renderExpanded,
		loading = false
	},
	props
) {
	const classes = useStyles(props);
	const theme = useTheme(); // Add useTheme hook

	const [order, setOrder] = React.useState('desc');
	const [orderBy, setOrderBy] = React.useState(headCells[0].id);
	const [page, setPage] = React.useState(curPage || 0);
	const [rowsPerPage, setRowsPerPage] = React.useState(perPageRows || 5);
	const [expandedKeys, setExpandedKeys] = React.useState(() => new Set());

	const toggleExpanded = key => {
		setExpandedKeys(prev => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const getRowKey = useCallback(
		row => {
			if (!rowKey) return row.id;
			if (typeof rowKey === 'function') return rowKey(row);
			return row[rowKey];
		},
		[rowKey]
	);

	const handleSelectAllClick = event => {
		if (event.target.checked) {
			const newSelected = rows.map(n => getRowKey(n));
			setSelectedRows(newSelected);
			return;
		}
		setSelectedRows([]);
	};

	const handleClick = (event, key) => {
		const selectedIndex = selectedRows.indexOf(key);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedRows, key);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedRows.slice(1));
		} else if (selectedIndex === selectedRows.length - 1) {
			newSelected = newSelected.concat(selectedRows.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedRows.slice(0, selectedIndex),
				selectedRows.slice(selectedIndex + 1)
			);
		}
		setSelectedRows(newSelected);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
		if (setCurrentParams) {
			setCurrentParams({ limit: rowsPerPage, page: newPage });
		}
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		if (setCurrentParams) {
			setCurrentParams({ limit: parseInt(event.target.value, 10), page: 0 });
		}
	};

	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const visibleRows = React.useMemo(() => {
		if (totalItems) {
			return rows;
		}

		return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
	}, [totalItems, page, rowsPerPage, rows]);

	const getValue = (row, id) => {
		const cellConfig = headCells.find(cell => cell.id === id);

		if (cellConfig?.customRow) {
			return cellConfig.customRow(row);
		}

		const keys = id.split('.');
		const value = keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), row);

		if (id === 'dt' && value) {
			return moment(value).format('DD-MM-YYYY hh:mm a');
		}
		if (id === 'edit') {
			return <EditIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={() => onEditClick(row)} />;
		}
		if (id === 'delete') {
			return <DeleteIcon style={{ cursor: 'pointer' }} fontSize="large" onClick={() => onDeleteClick(row)} />;
		}

		return value;
	};

	return (
		<Box sx={{ width: '100%', height: height, borderRadius: '15px' }} className={classes.tableContainer}>
			<Paper className={classes.paper} sx={{ width: '100%', mb: 2, boxShadow: 'none', height: '100%' }}>
				{tableName && <EnhancedTableToolbar numSelected={selectedRows.length} title={title} />}
				<div className={classes.tableWrapper}>
					<Table className={classes.table} aria-labelledby="tableTitle" size="medium" stickyHeader>
						<EnhancedTableHead
							numSelected={selectedRows?.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={rows.length}
							headCells={headCells}
							checkBox={checkBox}
							expandable={expandable}
						/>
						<TableBody>
							{loading
								? Array.from({ length: rowsPerPage }).map((_, skeletonIndex) => (
										<TableRow key={`skeleton-${skeletonIndex}`}>
											{expandable && <TableCell padding="checkbox" />}
											{checkBox && (
												<TableCell padding="checkbox">
													<Skeleton variant="rect" width={20} height={20} animation="wave" />
												</TableCell>
											)}
											{headCells.map(cell => (
												<TableCell
													classes={{ root: classes.tableCell }}
													key={cell.id}
													align={cell.numeric ? 'center' : 'left'}
												>
													<Skeleton variant="text" width="80%" height={20} animation="wave" />
												</TableCell>
											))}
										</TableRow>
								  ))
								: visibleRows.map((row, index) => {
								const key = getRowKey(row);
								const isItemSelected = selectedRows?.includes(key);
								const isExpanded = expandedKeys.has(key);
								const labelId = `enhanced-table-checkbox-${index}`;
								const colSpanTotal = headCells.length + (checkBox ? 1 : 0) + (expandable ? 1 : 0);

								return (
									<React.Fragment key={key}>
										<TableRow
											hover
											onClick={checkBox ? event => handleClick(event, key) : null}
											role="checkbox"
											aria-checked={isItemSelected}
											tabIndex={-1}
											selected={isItemSelected}
											sx={{ cursor: 'pointer' }}
										>
											{expandable && (
												<TableCell padding="checkbox">
													<IconButton
														size="small"
														onClick={event => {
															event.stopPropagation();
															toggleExpanded(key);
														}}
													>
														{isExpanded ? (
															<KeyboardArrowDownIcon fontSize="small" />
														) : (
															<KeyboardArrowRightIcon fontSize="small" />
														)}
													</IconButton>
												</TableCell>
											)}
											{checkBox && (
												<TableCell padding="checkbox">
													<Checkbox
														checked={isItemSelected}
														inputProps={{
															'aria-labelledby': labelId
														}}
														className={classes.checkbox}
														style={{
															color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
														}}
													/>
												</TableCell>
											)}
											{headCells.map(cell => (
												<TableCell
													classes={{ root: classes.tableCell }}
													sx={{ fontSize: '1.1rem', paddingLeft: 0 }}
													key={cell.id}
													align={cell.numeric ? 'center' : 'left'}
												>
													{getValue(row, cell.id)}
												</TableCell>
											))}
										</TableRow>
										{expandable && (
											<TableRow>
												<TableCell style={{ padding: 0, border: isExpanded ? undefined : 'none' }} colSpan={colSpanTotal}>
													<Collapse in={isExpanded} timeout="auto" unmountOnExit>
														{renderExpanded ? renderExpanded(row) : null}
													</Collapse>
												</TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				</div>
				{!isLoadingPagination && (
					<div className={classes.pagination}>
						<TablePagination
							rowsPerPageOptions={rowsPerPageOptions}
							component="div"
							count={totalItems || rows.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							ActionsComponent={PaginationActionsTableOmni}
						/>
					</div>
				)}
			</Paper>
		</Box>
	);
}

TableOmni.propTypes = {
	rows: PropTypes.array.isRequired,
	headCells: PropTypes.array.isRequired,
	title: PropTypes.string,
	height: PropTypes.string
};

export default TableOmni;